import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, CheckCircle2, Skull, Crown, Sparkles, AlertCircle, Timer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '@/config/api';

const InterrogationChat = ({ user, commesse, onEntryCreated, refetchCommesse }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [pendingTracking, setPendingTracking] = useState(null);
  const [conversationContext, setConversationContext] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const getGreeting = async () => {
      try {
        const response = await axios.get(`${API}/don-greeting?user_id=${user.id}`);
        setMessages([{
          type: 'don',
          text: response.data.greeting,
          threatLevel: 1
        }]);
      } catch (err) {
        setMessages([{
          type: 'don',
          text: '🤌 Eeeeeh compare! Finalmente ti fai vivo! Il Padrino stava già mandando i ragazzi a cercarti...',
          threatLevel: 1
        }]);
      }
    };
    getGreeting();
  }, [user.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);

    // Check for confirmation keywords
    const isConfirmation = ['salva', 'conferma', 'ok', 'vai', 'perfetto', 'procedi', 'si', 'sì', 'yes', 'certo'].some(kw =>
      userMessage.toLowerCase().includes(kw)
    );

    if (isConfirmation) {
      if (pendingTracking) {
        confirmTracking();
        return;
      }
      if (pendingEntries.length > 0) {
        confirmEntries();
        return;
      }
      // If keyword matched but nothing to confirm, don't just hang or call AI
      if (userMessage.toLowerCase() === 'salva' || userMessage.toLowerCase() === 'conferma') {
        setMessages(prev => [...prev, {
          type: 'don',
          text: '🤌 Non ho niente da salvare, soldato! Prima dimmi cosa hai fatto...',
          threatLevel: 2
        }]);
        return;
      }
    }

    setLoading(true);

    try {
      // Build conversation history for context
      const recentMessages = messages.slice(-4).map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: m.text
      }));

      const response = await axios.post(`${API}/parse-entry`, {
        text: userMessage,
        user_id: user.id,
        user_name: user.name,
        conversation_history: recentMessages,
        context: conversationContext
      });

      const { entries, missing_info, threat_level, response_message, context, newly_created } = response.data;

      // Update context
      if (context) {
        setConversationContext(context);
      }

      // If AI created new commesse, refresh the list immediately
      if (newly_created && newly_created.length > 0 && refetchCommesse) {
        refetchCommesse();
      }

      setMessages(prev => [...prev, {
        type: 'don',
        text: response_message,
        threatLevel: threat_level,
        entries: entries,
        intent: response.data.intent
      }]);

      if (response.data.intent === 'start_tracking' && entries && entries.length > 0 && missing_info.length === 0) {
        setPendingTracking(entries[0]);
      } else if (entries && entries.length > 0 && missing_info.length === 0) {
        setPendingEntries(entries);
      }

    } catch (err) {
      console.error('Error parsing entry details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });

      const isNetworkError = !err.response && !window.navigator.onLine;
      const errorText = isNetworkError
        ? '🤌 Maledizione! C\'è silenzio radio! Controlla la tua connessione, soldato!'
        : '💀🔥 MADONNA MIA! Qualcosa è andato storto! Il sistema ha avuto un... "incidente". Riprova!';

      setMessages(prev => [...prev, {
        type: 'don',
        text: errorText,
        threatLevel: 5
      }]);
    } finally {
      setLoading(false);
    }
  };

  const confirmEntries = async () => {
    setLoading(true);
    let successCount = 0;

    for (const entry of pendingEntries) {
      try {
        await axios.post(`${API}/time-entries`, {
          user_id: user.id,
          user_name: user.name,
          date: entry.data,
          commessa_code: entry.commessa_code,
          commessa_name: entry.commessa_name,
          project_id: null,
          project_name: entry.project_name || null,
          description: entry.description,
          hours: entry.ore,
          notes: null
        });
        successCount++;
      } catch (err) {
        console.error('Error creating entry:', err);
      }
    }

    setMessages(prev => [...prev, {
      type: 'don',
      text: `🎉👑✨ MAGNIFICO COMPARE! ${successCount} registrazion${successCount === 1 ? 'e' : 'i'} salvat${successCount === 1 ? 'a' : 'e'}! Il Padrino è ESTASIATO! 🍝🍷`,
      threatLevel: 1
    }]);

    setPendingEntries([]);
    setLoading(false);
    setConversationContext({}); // Reset context after successful entry
    if (refetchCommesse) refetchCommesse();
    onEntryCreated();
  };

  const confirmTracking = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/tracking/start`, {
        user_id: user.id,
        user_name: user.name,
        commessa_code: pendingTracking.commessa_code,
        commessa_name: pendingTracking.commessa_name,
        project_name: pendingTracking.project_name,
        description: pendingTracking.description,
        estimated_hours: pendingTracking.ore
      });

      setMessages(prev => [...prev, {
        type: 'don',
        text: `⏳ Mhh, vedremo, ${pendingTracking.ore} ore dici? Ci sentiamo alla fine... NON DELUDERMI! 💀🔥`,
        threatLevel: 1
      }]);

      setPendingTracking(null);
      setConversationContext({}); // Reset context after starting tracking
      if (refetchCommesse) refetchCommesse();
      onEntryCreated(); // Trigger refresh to show timer on dashboard
    } catch (err) {
      console.error('Error starting tracking:', err);
      toast.error(err.response?.data?.detail || "Errore nell'avvio del tracking");
    } finally {
      setLoading(false);
    }
  };

  const getThreatStyle = (level) => {
    switch (level) {
      case 1: return 'border-amber-500/30 bg-amber-500/5';
      case 2: return 'border-yellow-500/30 bg-yellow-500/5';
      case 3: return 'border-orange-500/30 bg-orange-500/5';
      case 4: return 'border-red-500/30 bg-red-500/5';
      case 5: return 'border-red-600/50 bg-red-600/10 animate-pulse';
      default: return '';
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center items-center gap-2 mb-4">
          <Skull className="w-8 h-8 text-red-500" />
          <Crown className="w-10 h-10 text-amber-400" />
          <Skull className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="font-display text-4xl font-black text-white mb-2">IL DON PARLA</h2>
        <p className="text-zinc-500 italic">"Dimmi cosa hai fatto... e che sia la verità!"</p>
      </div>

      {/* Chat Area */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl mb-4 h-[400px] overflow-hidden">
        <div className="h-full overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
            >
              <div
                className={`max-w-[85%] rounded-2xl p-4 ${message.type === 'user'
                  ? 'bg-primary text-black rounded-br-sm'
                  : `bg-zinc-800 border ${getThreatStyle(message.threatLevel)} rounded-bl-sm`
                  }`}
              >
                {message.type === 'don' && (
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
                    <Skull className="w-4 h-4 text-amber-400" />
                    <span className="text-xs text-amber-400 font-display uppercase tracking-wider">Il Don</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {message.text}
                </p>

                {message.entries && message.entries.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-zinc-400 font-semibold uppercase flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Dati Estratti
                    </p>
                    {message.entries.map((entry, i) => (
                      <div key={i} className="bg-black/30 rounded-xl p-3 border border-white/5">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            📅 {entry.data}
                          </Badge>
                          <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                            {entry.commessa_name}
                          </Badge>
                          {entry.project_name && (
                            <Badge variant="secondary" className="text-xs">
                              📁 {entry.project_name}
                            </Badge>
                          )}
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                            ⏱️ {entry.ore}h
                          </Badge>
                        </div>
                        <p className="text-xs text-zinc-500">{entry.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-zinc-800 rounded-2xl p-4 border border-zinc-700 rounded-bl-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                  <span className="text-sm text-zinc-400 italic">Il Don sta pensando...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Pending Tracking Confirmation */}
      {pendingTracking && (
        <div className="bg-amber-950/50 border-2 border-amber-500/30 rounded-2xl p-5 mb-4 animate-pulse">
          <div className="flex items-center gap-2 mb-4">
            <Timer className="w-5 h-5 text-amber-400" />
            <p className="font-bold text-amber-400 uppercase tracking-wider">Inizia Tracciamento Attivo</p>
          </div>
          <div className="bg-black/30 rounded-xl p-3 mb-4">
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                {pendingTracking.commessa_name}
              </Badge>
              <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                ⏱️ {pendingTracking.ore}h stimate
              </Badge>
            </div>
            <p className="text-sm text-zinc-400">{pendingTracking.description}</p>
          </div>
          <div className="flex gap-3">
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-black font-bold py-6"
              onClick={confirmTracking}
              disabled={loading}
              data-testid="confirm-tracking-button"
            >
              <Timer className="w-5 h-5 mr-2" />
              INIZIA ORA 🤌
            </Button>
            <Button
              variant="outline"
              onClick={() => setPendingTracking(null)}
              disabled={loading}
              className="border-zinc-700 text-zinc-400"
            >
              Annulla
            </Button>
          </div>
          <p className="text-[10px] text-amber-500/50 text-center mt-3 italic uppercase tracking-tighter">
            "Il Don ti osserva... non far scadere il tempo"
          </p>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Parla al Don... con rispetto! 🤌"
          className="flex-1 h-14 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
          disabled={loading}
          data-testid="chat-input"
        />
        <Button
          type="submit"
          className="h-14 px-6 bg-primary hover:bg-primary/90 text-black font-bold"
          disabled={loading || !input.trim()}
          data-testid="send-button"
        >
          <Send className="w-5 h-5" />
        </Button>
      </form>

      {/* Examples */}
      <div className="mt-6 text-center">
        <p className="text-xs text-zinc-600 mb-3 flex items-center justify-center gap-2">
          <AlertCircle className="w-3 h-3" />
          Esempi di cosa dire:
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {[
            { text: "Oggi 4 ore su Procida", full: "Oggi ho fatto 4 ore su Procida per montaggio video" },
            { text: "Ieri 3 ore Podcastory Tunisia", full: "Ieri ho lavorato 3 ore su Podcastory sul progetto Tunisia" },
            { text: "2 ore Alice, 3 ore BusDev", full: "Oggi 2 ore su Alice e 3 ore business development" },
          ].map((example, i) => (
            <button
              key={i}
              onClick={() => setInput(example.full)}
              className="text-xs px-3 py-2 rounded-lg bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:border-primary/50 hover:text-primary transition-colors"
            >
              "{example.text}"
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InterrogationChat;
